/**
 * Atlas — encode/decode a `Scope` (+ difficulty) into URL parameters.
 *
 * The hub deep-links into `/play/atlas?mode=daily&scope=<scope>&level=<difficulty>`;
 * the module entry decodes them back. The scope format is compact + readable:
 *
 *   world                 → { kind: 'world' }
 *   landmarks             → { kind: 'landmarks' }
 *   continent:Europe      → { kind: 'continent', continent: 'Europe' }
 *   country:FR            → { kind: 'country', countryCode: 'FR' }
 *   state:CA              → { kind: 'usState', stateCode: 'CA' }
 *
 * Difficulty rides a separate `level` param (`classic` | `explorer` |
 * `adventurer`) and is OMITTED for Landmarks. Decoding is defensive: an unknown
 * or malformed scope falls back to World, and an unknown/absent level falls back
 * to `classic` for a regional scope (the contract's default) and `undefined` for
 * Landmarks — so a stale link can never crash the game.
 */
import { CONTINENTS, DIFFICULTIES, type Continent, type Difficulty, type Scope } from './schema';

export function encodeScope(scope: Scope): string {
  switch (scope.kind) {
    case 'world':
      return 'world';
    case 'landmarks':
      return 'landmarks';
    case 'continent':
      return `continent:${scope.continent}`;
    case 'country':
      return `country:${scope.countryCode.toUpperCase()}`;
    case 'usState':
      return `state:${scope.stateCode.toUpperCase()}`;
  }
}

export function decodeScope(param: string | null | undefined): Scope {
  if (!param) return { kind: 'world' };
  const raw = decodeURIComponent(param);
  if (raw === 'world') return { kind: 'world' };
  if (raw === 'landmarks') return { kind: 'landmarks' };

  const [kind, value] = raw.split(':');
  if (kind === 'continent' && value) {
    const match = CONTINENTS.find((c) => c === value);
    if (match) return { kind: 'continent', continent: match as Continent };
  }
  if (kind === 'country' && value) {
    return { kind: 'country', countryCode: value.toUpperCase() };
  }
  if (kind === 'state' && value) {
    return { kind: 'usState', stateCode: value.toUpperCase() };
  }
  return { kind: 'world' };
}

/** The URL value for a difficulty (Landmarks → empty: no `level` param). */
export function encodeDifficulty(difficulty: Difficulty | undefined): string {
  return difficulty ?? '';
}

/**
 * Decode the `level` param against a scope. Landmarks always yields `undefined`
 * (no difficulty dimension). A regional scope yields the named tier when valid,
 * else the contract default `classic` — so a malformed or absent level never
 * crashes nor leaves a regional round difficulty-less.
 */
export function decodeDifficulty(
  scope: Scope,
  param: string | null | undefined,
): Difficulty | undefined {
  if (scope.kind === 'landmarks') return undefined;
  const raw = param ? decodeURIComponent(param).toLowerCase() : '';
  const match = DIFFICULTIES.find((d) => d === raw);
  return match ?? 'classic';
}
